import { z } from "zod";
import { KostenartSchema, StuecklisteSchema } from "./common.js";

/**
 * Tenant-Stammdaten — Kundenartikel + Konditionsmatrix.
 *
 * Liegen unter `.bidi-handel/tenants/<tenantId>/stammdaten/{kundenartikel,konditionsmatrix}.json`.
 * Im MVP werden sie manuell gepflegt; H8 bringt ERP-Konnektor-Imports.
 *
 * Schema bewusst tolerant: was H4 nicht braucht, ist optional. Frank's Pilot-
 * Händler kann später Felder ergänzen, ohne dass Bestandsdaten brechen.
 */

export const KundenartikelSchema = z.object({
  /** Eindeutige Artikel-Nummer im Tenant. Wird als Fremdschlüssel in
   *  04-artikel-match.json referenziert. */
  code: z.string().min(1),
  bezeichnung: z.string().min(1),
  /** Kurzbeschreibung — wird im Pre-Filter mit der LV-Position verglichen. */
  kurzText: z.string().optional(),
  ean: z.string().optional(),
  vpe: z.string().optional(),
  /** Listenpreis in EUR pro Einheit. Optional weil manche Stammdaten nur
   *  Konditionspreise tragen. */
  listenpreis: z.number().nonnegative().optional(),
  /** Verkaufs-/Preiseinheit. `listenpreis` ist pro `einheit`. */
  einheit: z.string().optional(),
  /** Physikalische Basiseinheit + Inhalt pro VE (Schritt 2): ermöglicht die
   *  Gebinde→Basis-Umrechnung in der Bepreisung. Fehlt es → Preis pro `einheit`. */
  basiseinheit: z.string().optional(),
  inhaltProVE: z.number().positive().optional(),
  /** Warengruppe als Freitext-Schlüssel (für Konditionsmatrix-Lookup +
   *  optionalen Pre-Filter). */
  warengruppe: z.string().optional(),
  /** Synonyme/Suchbegriffe — werden zusätzlich zur Bezeichnung in den
   *  Token-Index aufgenommen. Hilft wenn Architekten-LV andere Wörter
   *  nutzt als die Hersteller-Bezeichnung. */
  suchbegriffe: z.array(z.string()).default([]),
  /** Montage-Stückliste (Set) — vom Artikelstamm (GlobalArtikel) durch die
   *  effektive Projektion durchgereicht (buildEffectiveStammdaten). Ist dieser
   *  Artikel der gewählte Artikel einer Position, expandiert die Match-Phase
   *  die Stückliste zur Schnellkalkulation. Optional. */
  stueckliste: StuecklisteSchema.optional(),
  /** Alles weitere passthrough damit Pilot-Daten nicht brechen. */
});
export type Kundenartikel = z.infer<typeof KundenartikelSchema>;

export const KundenartikelListSchema = z.array(KundenartikelSchema);
export type KundenartikelList = z.infer<typeof KundenartikelListSchema>;

/**
 * Konditionsmatrix-Eintrag. Pro Kunde × Warengruppe ein Rabatt
 * (Prozent vom Listenpreis) ODER ein Sonderpreis (überschreibt Listenpreis).
 * Beide gleichzeitig sind ein Konfig-Fehler — wird im Bepreisungs-Agent (H5)
 * geprüft.
 */
export const KonditionsmatrixEntrySchema = z
  .object({
    kundeId: z.string(),
    warengruppe: z.string().optional(),
    artikelCode: z.string().optional(),
    rabattProzent: z.number().min(0).max(100).optional(),
    sonderpreis: z.number().nonnegative().optional(),
    bemerkung: z.string().optional(),
  })
  .refine(
    (v) => v.warengruppe !== undefined || v.artikelCode !== undefined,
    "Konditionsmatrix-Eintrag braucht entweder warengruppe oder artikelCode"
  );
export type KonditionsmatrixEntry = z.infer<typeof KonditionsmatrixEntrySchema>;

export const KonditionsmatrixSchema = z.array(KonditionsmatrixEntrySchema);
export type Konditionsmatrix = z.infer<typeof KonditionsmatrixSchema>;

/**
 * Hilfsartikel — wiederverwendbare Vorlagen fuer Schnellkalkulations-Zeilen,
 * die keine echten Material-Kundenartikel sind (z.B. Lohn-Std, Geraete-
 * Stunden, Container-Miete, Entsorgungs-Pauschalen).
 *
 * Bewusst eigenes Schema und nicht erweitertes `KundenartikelSchema`,
 * damit Catalog/Sortiment/Match-Agent-Pre-Filter/Bepreisungs-Logik
 * unveraendert mit „echten Artikeln" arbeiten und Hilfsartikel-Eintraege
 * niemals als Materialvorschlag im Phase-4-Match auftauchen.
 *
 * Liegt unter `.bidi-handel/tenants/<tenantId>/stammdaten/hilfsartikel.json`,
 * Default leere Liste wenn die Datei nicht existiert.
 */
export const HilfsartikelSchema = z.object({
  code: z.string().min(1),
  bezeichnung: z.string().min(1),
  einheit: z.string().min(1),
  defaultPreis: z.number().nonnegative(),
  defaultKostenart: KostenartSchema,
  bemerkung: z.string().optional(),
});
export type Hilfsartikel = z.infer<typeof HilfsartikelSchema>;

export const HilfsartikelListSchema = z.array(HilfsartikelSchema);
export type HilfsartikelList = z.infer<typeof HilfsartikelListSchema>;
